import { useEffect, useRef, useState } from 'react';
import { Text } from '~/components/ui/Text';
import { rage, useObservable } from '~/rage';
import { CaseItemRarity, CaseType, WalletType } from '~/shared';
import {
	setActiveCase,
	setShopOpenCaseVisible,
	setTabIndex,
	useLayoutDispatch,
} from '../../redux';
import { CaseFeedItem } from '../CaseFeedItem';
import {
	CaseItem,
	CaseItemColors,
	CaseType as CaseTypes,
	calcOldPrice,
} from '../CaseItem';
import {
	StyledCasesContainer,
	StyledContainer,
	StyledEmptyContainer,
	StyledFeedContainer,
	StyledFeedItemWrapper,
	StyledFeedTrack,
} from './ShopCases.styled';

const ShopCases = () => {
	const dispatch = useLayoutDispatch();
	const cases = useObservable(rage.cases.getLootboxes());
	const caseFeed = useObservable(rage.cases.getCaseFeed());

	const [feed, setFeed] = useState<
		Array<{
			id: string;
			color: string;
			title: string;
			rewardId: string;
			rewardImageId: string;
			rewardType: ReturnType<typeof rage.cases.getRewardById>['type'];
			lootboxId: string;
		}>
	>([]);

	const [isHovered, setIsHovered] = useState(false);
	const trackRef = useRef<HTMLDivElement | null>(null);
	const firstItemWidthRef = useRef<number>(0);
	const pendingEnterRef = useRef<Set<string>>(new Set());
	const pendingQueueRef = useRef<
		Array<{
			id: string;
			color: string;
			title: string;
			rewardId: string;
			rewardImageId: string;
			rewardType: ReturnType<typeof rage.cases.getRewardById>['type'];
			lootboxId: string;
		}>
	>([]);
	const prevFirstIdRef = useRef<string | null>(null);

	const handleOpenCase = (lootboxId: string) => {
		try {
			const selectedCase = rage.cases.getLootboxById(lootboxId);
			const { getClientConfig: _getClientConfig, ...caseWithoutConfig } =
				selectedCase as any;
			dispatch(setActiveCase(caseWithoutConfig));
			dispatch(setTabIndex(1));
			dispatch(setShopOpenCaseVisible(true));
		} catch {
			return;
		}
	};

	const mapFeedItem = (item: (typeof caseFeed)[number]) => {
		const reward = rage.cases.getRewardById(item.rewardId);
		const rarity = item.rarity ?? CaseItemRarity.COMMON;
		const rewardType =
			reward?.type ?? rage.inventoryDonate.getTypeById(item.rewardId);
		const rewardImageId =
			rage.cases.getRewardImageId(item.rewardId) ?? item.rewardId;

		let title = '';
		try {
			title = rage.cases.getLootboxById(item.lootboxId)?.name ?? '';
		} catch {
			title = '';
		}

		return {
			id: item.id ?? `${Date.now()}_${Math.random().toString(16).slice(2)}`,
			color: CaseItemColors[rarity] ?? CaseItemColors[CaseItemRarity.COMMON],
			title,
			rewardId: item.rewardId,
			rewardImageId,
			rewardType,
			lootboxId: item.lootboxId,
		};
	};

	const addFeedItem = (item: {
		id: string;
		color: string;
		title: string;
		rewardId: string;
		rewardImageId: string;
		rewardType: ReturnType<typeof rage.cases.getRewardById>['type'];
		lootboxId: string;
	}) => {
		const track = trackRef.current;
		if (track && track.firstElementChild) {
			const firstChild = track.firstElementChild as HTMLElement;
			firstItemWidthRef.current = firstChild.offsetWidth + 10;
			track.style.transform = `translateX(${firstItemWidthRef.current}px)`;
		}

		setFeed((prev) => {
			const next = [item, ...prev].slice(0, 20);
			pendingEnterRef.current.add(item.id);
			return next;
		});

		requestAnimationFrame(() => {
			const track = trackRef.current;
			if (track) {
				track.style.transition = 'transform 0.35s ease';
				track.style.transform = 'translateX(0)';
				const handleEnd = () => {
					track.style.transition = '';
					track.removeEventListener('transitionend', handleEnd);
				};
				track.addEventListener('transitionend', handleEnd);
			}
		});
	};

	useEffect(() => {
		if (caseFeed.length === 0) {
			setFeed([]);
			prevFirstIdRef.current = null;
			pendingQueueRef.current = [];
			return;
		}

		const mapped = caseFeed.slice(0, 20).map(mapFeedItem);
		const nextFirstId = mapped[0]?.id ?? null;

		if (prevFirstIdRef.current === null) {
			setFeed(mapped);
			prevFirstIdRef.current = nextFirstId;
			return;
		}

		if (!nextFirstId || nextFirstId === prevFirstIdRef.current) {
			return;
		}

		const prevIndex = mapped.findIndex((x) => x.id === prevFirstIdRef.current);
		const newItems = prevIndex > 0 ? mapped.slice(0, prevIndex) : [mapped[0]];

		if (isHovered) {
			pendingQueueRef.current.push(...newItems);
		} else {
			newItems
				.slice()
				.reverse()
				.forEach((newItem, idx) => {
					setTimeout(() => addFeedItem(newItem), idx * 100);
				});
		}

		prevFirstIdRef.current = nextFirstId;
	}, [caseFeed, isHovered]);

	useEffect(() => {
		if (!isHovered && pendingQueueRef.current.length > 0) {
			const queued = pendingQueueRef.current.slice();
			pendingQueueRef.current = [];
			queued
				.slice()
				.reverse()
				.forEach((newItem, idx) => {
					setTimeout(() => addFeedItem(newItem), idx * 100);
				});
		}
	}, [isHovered]);

	return (
		<StyledContainer>
			<StyledFeedContainer
				onMouseEnter={() => setIsHovered(true)}
				onMouseLeave={() => setIsHovered(false)}
			>
				<StyledFeedTrack ref={trackRef}>
					{feed.map((item) => (
						<StyledFeedItemWrapper
							key={item.id}
							data-enter={pendingEnterRef.current.has(item.id)}
							onAnimationEnd={() => pendingEnterRef.current.delete(item.id)}
						>
							<CaseFeedItem
								color={item.color}
								title={item.title}
								rewardId={item.rewardId}
								rewardImageId={item.rewardImageId}
								rewardType={item.rewardType}
								onOpen={() => handleOpenCase(item.lootboxId)}
							/>
						</StyledFeedItemWrapper>
					))}
				</StyledFeedTrack>
			</StyledFeedContainer>
			{cases.length === 0 && (
				<StyledEmptyContainer>
					<Text>
						К сожалению,
						<br />
						доступных кейсов нет
					</Text>
				</StyledEmptyContainer>
			)}
			<StyledCasesContainer>
				{[...cases].map((item, index) => (
					<CaseItem
						id={item.id}
						name={item.name}
						type={
							rage.cases.getCaseTypeById(item.id) === CaseType.CASE
								? CaseTypes.Default
								: CaseTypes.Kit
						}
						span={rage.cases.getCaseTypeById(item.id) === CaseType.CASE ? 1 : 2}
						image={item.icon}
						discount={item.discount ?? 0}
						price={item.price ?? 0}
						wallet={item.walletType as WalletType.Cash | WalletType.Donate}
						oldPrice={calcOldPrice(item.price ?? 0, item.discount ?? 0)}
						key={index}
						tags={item.tags}
						dateDiscountEnd={item.discountTimestampEnd}
						dateEnd={item.enabledTimestampEnd}
					/>
				))}
			</StyledCasesContainer>
		</StyledContainer>
	);
};

export { ShopCases };
